import type { UploadGrant } from './api'

/**
 * Putting the archive in the bucket.
 *
 * ── IT IS A POST, NOT A PUT, AND THE FIELDS ARE THE POINT ───────────────────
 *
 * The grant is a **presigned POST policy**: a form whose every field must be sent
 * verbatim, in the order given, before the file part. The signature is over the
 * whole form rather than over the URL, so dropping or reordering one field
 * invalidates it — and one of those fields is `content-length-range`, which is
 * what makes S3 itself refuse an oversized upload rather than the API having to
 * take the bytes first and complain afterwards.
 *
 * ── AND IT IS `XMLHttpRequest`, WHICH IS NOT AN OVERSIGHT ────────────────────
 *
 * `fetch` has no upload-progress event. There is no option, no stream shim that
 * works across the browsers this ships to, and no way to synthesise one that is
 * not a lie about how far the bytes have got. §2.1 asks for progress during the S3
 * step to come "from the browser's upload events", and `xhr.upload.onprogress` is
 * the only thing that emits them. Everything else in this app uses `fetch`; this
 * one file does not, and this is why.
 */

export interface UploadProgress {
  loaded: number
  total: number
  /** 0–100, floored. Presentation only — nothing downstream reads it. */
  percent: number
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void
  /**
   * Abort the browser upload. NOT a cancellation of anything server-side.
   *
   * ── NO PRODUCTION CALLER, DELIBERATELY, AND RECORDED RATHER THAN QUIETLY
   * DEAD ────────────────────────────────────────────────────────────────────
   *
   * `Record.tsx` passes `onProgress` and nothing else. No screen offers a Stop
   * control, so this parameter, `UploadAbortedError` and the `abort` listener
   * below are reachable only from `upload.test.ts` -- trap 20's exact shape, in
   * a file whose test count says otherwise.
   *
   * Kept rather than deleted because a Stop affordance is a §2.8 feature
   * somebody will want and the plumbing is correct; but it is not covered by
   * anything a user can do, and the tests for it prove only that the function
   * behaves if something ever calls it. Do not read its test count as coverage.
   */
  signal?: AbortSignal
  /**
   * How long the upload may make NO PROGRESS before it is reported.
   *
   * `fetchWithTimeout`'s 15s (`api.ts:59`) is on `fetch` and never applied
   * here, and `onerror` covers a connection that RESETS -- not one that stalls.
   * So a half-open connection left "Uploading" on screen indefinitely with no
   * message and nothing to press, against §2.8's "pause with a recoverable
   * message on network failure".
   *
   * ── NOT `xhr.timeout`, AND THE FIRST VERSION OF THIS GOT IT WRONG ─────────
   *
   * `xhr.timeout` is a TOTAL deadline measured from `send()`, and no engine
   * resets it on progress -- so setting it to two minutes put a two-minute cap
   * on the whole upload, which a multi-megabyte archive on a phone connection
   * exceeds honestly. It would have aborted healthy uploads that used to
   * succeed, which is a worse failure than the one it was fixing, and the
   * comment beside it asserted inactivity semantics the platform does not have.
   * Three reviewers found it independently.
   *
   * So the timer is ours, and it is re-armed on every progress event. Trap 3
   * bears on this: `xhr.upload.onprogress` never fires under a service worker,
   * so in any fixture-backed run this degrades to a total deadline. Real
   * uploads go straight to S3 with no worker in the path.
   */
  timeoutMs?: number
}

/** Two minutes of NO progress. Re-armed on every progress event. */
export const UPLOAD_STALL_TIMEOUT_MS = 120_000

export class UploadFailedError extends Error {
  readonly status: number | null
  /** S3 answers with an XML error document; kept whole for the log, not the UI. */
  readonly body: string | null

  constructor(message: string, status: number | null, body: string | null = null) {
    super(message)
    this.name = 'UploadFailedError'
    this.status = status
    this.body = body
  }
}

export class UploadAbortedError extends Error {
  constructor() {
    super('The upload was stopped.')
    this.name = 'UploadAbortedError'
  }
}

/** The grant is time-limited; check before spending minutes sending bytes. */
export function grantHasExpired(grant: UploadGrant, now: number = Date.now()): boolean {
  const expiry = Date.parse(grant.expires_at)
  return Number.isFinite(expiry) && expiry <= now
}

/**
 * Whether this file can possibly be accepted.
 *
 * A local check against the grant's own `max_bytes`, so a user is told before a
 * long upload rather than after one. **It is not the authority** — the policy
 * enforces the same cap at S3 and the API re-checks the archive's contents after
 * that — and it never trims or rewrites the file to make it fit, which §2.7
 * rules out in as many words.
 */
export function fileExceedsGrant(file: File, grant: UploadGrant): boolean {
  return file.size > grant.max_bytes
}

/**
 * Send the file under the grant's policy.
 *
 * Resolves when S3 has taken the bytes. That is **not** the same as the recording
 * existing: the archive is still unopened at this point, and the job that reads it
 * has not been submitted.
 */
export function uploadToGrant(
  grant: UploadGrant,
  file: File,
  { onProgress, signal, timeoutMs = UPLOAD_STALL_TIMEOUT_MS }: UploadOptions = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadAbortedError())
      return
    }

    const form = new FormData()
    // Every policy field, verbatim and BEFORE the file. S3 ignores form fields
    // that follow the file part, so a `file` appended first silently invalidates
    // a policy that was otherwise correct.
    for (const [name, value] of Object.entries(grant.fields ?? {})) {
      form.append(name, value)
    }
    form.append('file', file)

    const xhr = new XMLHttpRequest()
    xhr.open(grant.method, grant.url, true)

    /**
     * `stalled` separates our abort from the caller's: both arrive at
     * `onabort`, and they are different outcomes. A stall is a failure the user
     * should be told about and offered a retry for; a caller's abort is
     * something they asked for.
     */
    let stalled = false
    let stallTimer: ReturnType<typeof setTimeout> | undefined
    const armStall = () => {
      clearTimeout(stallTimer)
      stallTimer = setTimeout(() => { stalled = true; xhr.abort() }, timeoutMs)
    }
    // Headers the grant asked for. Content-Type is NOT set here: the browser has
    // to write the multipart boundary into it, and overriding that produces a
    // body S3 cannot parse.
    for (const [name, value] of Object.entries(grant.headers ?? {})) {
      xhr.setRequestHeader(name, value)
    }

    const onAbort = () => xhr.abort()
    signal?.addEventListener('abort', onAbort, { once: true })
    const done = () => {
      clearTimeout(stallTimer)
      signal?.removeEventListener('abort', onAbort)
    }

    // Re-armed even when the caller wants no progress callback: the timer is
    // about the connection, not about the UI.
    xhr.upload.onprogress = (event) => {
      armStall()
      if (!onProgress) return
      if (event.lengthComputable) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percent: Math.floor((event.loaded / event.total) * 100),
        })
      }
    }

    xhr.onload = () => {
      done()
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
        return
      }
      reject(
        new UploadFailedError(
          `The upload was refused (${xhr.status}).`,
          xhr.status,
          typeof xhr.responseText === 'string' ? xhr.responseText : null,
        ),
      )
    }

    xhr.onerror = () => {
      done()
      // No status, no body: a DNS failure, a dropped connection, a CORS refusal.
      // Indistinguishable from each other here, and all of them retryable.
      reject(new UploadFailedError('The upload could not reach the server.', null))
    }

    xhr.onabort = () => {
      done()
      // Deliberately an `UploadFailedError` for a stall and not a new class: to
      // the screen this is the same situation as a dropped connection --
      // nothing arrived, nothing was refused, and pressing the button again is
      // the right move. `status: null` is what `Record.tsx` already branches on.
      reject(stalled
        ? new UploadFailedError('The upload stopped making progress.', null)
        : new UploadAbortedError())
    }

    xhr.send(form)
    armStall()
  })
}
